import React, { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import CoachCapacityPanel from "@/components/CoachCapacityPanel";

const RANGES = [
  { k: "day", l: "Heute" },
  { k: "week", l: "Diese Woche" },
  { k: "month", l: "Dieser Monat" },
];

const AMPEL = {
  green: { color: "bg-emerald-500", label: "Im Plan" },
  yellow: { color: "bg-[#FFD100]", label: "Leicht hinten" },
  red: { color: "bg-red-600", label: "Deutlich hinten" },
  none: { color: "bg-slate-300", label: "Keine aktiven Kunden" },
};

function GoalAmpel({ goal }) {
  const g = goal || { status: "none", gap_pct: null };
  const meta = AMPEL[g.status] || AMPEL.none;
  const title = g.gap_pct != null ? `${meta.label} · ${g.gap_pct} Prozentpunkte Rückstand` : meta.label;
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${meta.color}`}
      title={title}
      data-testid={`ue-report-ampel-${g.status}`}
    />
  );
}

function Table({ title, rows, showAmpel }) {
  return (
    <div className="border border-slate-200 bg-white mb-6">
      <div className="px-5 py-3 border-b border-slate-200 text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center justify-between">
        <span>{title}</span>
        {showAmpel && (
          <div className="flex items-center gap-3 text-[10px] font-normal normal-case tracking-normal text-slate-500" data-testid="ue-report-ampel-legend">
            {Object.entries(AMPEL).filter(([k]) => k !== "none").map(([k, meta]) => (
              <span key={k} className="flex items-center gap-1">
                <span className={`inline-block w-2 h-2 rounded-full ${meta.color}`} /> {meta.label}
              </span>
            ))}
          </div>
        )}
      </div>
      <table className="min-w-full text-sm" data-testid={`ue-report-table-${title}`}>
        <thead>
          <tr className="border-b border-slate-200 text-[10px] font-bold uppercase tracking-overline text-slate-500">
            {showAmpel && <th className="text-left p-3 w-8"></th>}
            <th className="text-left p-3">Name</th>
            <th className="text-right p-3">UE (unterschrieben)</th>
            <th className="text-right p-3">Stunden</th>
            <th className="text-right p-3">Termine/Tage</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={showAmpel ? 5 : 4} className="p-6 text-center text-slate-400 italic">Keine Daten in diesem Zeitraum</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.user_id} className="border-b border-slate-100" data-testid={`ue-report-row-${r.user_id}`}>
              {showAmpel && <td className="p-3"><GoalAmpel goal={r.goal_status} /></td>}
              <td className="p-3 text-slate-900">{r.name}</td>
              <td className="p-3 text-right font-mono text-[#0a788c] font-medium">{r.ue}</td>
              <td className="p-3 text-right font-mono text-slate-600">{r.hours}h</td>
              <td className="p-3 text-right font-mono text-slate-600">{r.sessions}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function UeSignedReportTab() {
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/ue-signed-summary", { params: { range } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [range]);

  return (
    <div data-testid="mgmt-ue-bericht">
      <div className="flex items-center gap-3 mb-2">
        <CalendarClock size={14} strokeWidth={1.5} className="text-slate-500" />
        <p className="text-xs text-slate-500 max-w-xl">
          Zeigt nur UE, die der Kunde/Teilnehmer tatsächlich digital unterschrieben hat — ersetzt die
          alte, unterschriftslose Schnellerfassung ("Einträge").
        </p>
      </div>
      <div className="flex gap-1 mb-6 border-b border-slate-200" data-testid="ue-report-range-tabs">
        {RANGES.map(({ k, l }) => (
          <button key={k} onClick={() => setRange(k)} data-testid={`ue-report-range-${k}`}
            className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px ${range === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            {l}
          </button>
        ))}
      </div>
      {!data ? (
        <div className="text-xs text-slate-400 italic py-4">Lädt…</div>
      ) : (
        <>
          <div className="text-[11px] text-slate-500 mb-4 font-mono">{data.start_date} – {data.end_date}</div>
          <Table title="Coaches" rows={data.coaches} showAmpel />
          <Table title="Dozenten" rows={data.lecturers} />
          <CoachCapacityPanel />
        </>
      )}
    </div>
  );
}
